import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNavigateBack } from '../../../hooks/useNavigateBack';
import { useSupplyDelivery } from './useData';
import { moduleConfig } from './supplyDeliveriesModuleConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { Accordion, AccordionItem, Button, Card, CountChip, CheckIcon, ChevronLeftIcon } from '../../../components/ui';
import { moduleConfig as requisitionsModuleConfig } from '../requisitions/requisitionsModuleConfig';
import { supplyApiLineHasIssue } from './deliveryLineIssues';
import SupplyDeliveryShortfallAlert from './SupplyDeliveryShortfallAlert';

const formatDeliveryItemCell = (line: {
  itemVcode?: string | null;
  itemDescription?: string | null;
}) => {
  const v = (line.itemVcode ?? '').trim();
  const n = (line.itemDescription ?? '').trim();
  return [v, n].filter(Boolean).join(' - ');
};

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : NaN;
  const { item, isLoading, isError } = useSupplyDelivery(Number.isFinite(numericId) ? numericId : undefined);
  const { colors } = useTheme();
  const goBack = useNavigateBack(moduleConfig.module.route.list);
  const formatDate = (s?: string | null) => (s ? formatDateGlobal(s) : '');

  const hasDeliveryLineIssues = React.useMemo(() => {
    if (!item) return false;
    for (const line of item.lines || []) {
      if (supplyApiLineHasIssue(line)) return true;
    }
    return false;
  }, [item]);

  const newRequisitionNoCaseTo = React.useMemo(() => {
    if (!item || !hasDeliveryLineIssues) return null;
    const q = new URLSearchParams();
    q.set('noCase', '1');
    q.set('vesselId', String(item.vesselId));
    q.set('importFromDelivery', String(item.id));
    return `${requisitionsModuleConfig.module.route.new}?${q.toString()}`;
  }, [item, hasDeliveryLineIssues]);

  if (!Number.isFinite(numericId)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid id.</p>;
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center rounded-xl border border-dashed px-4 py-16', colors.border)}>
        <p className={cn('text-sm font-medium', colors.textSecondary)}>Loading supply delivery…</p>
      </div>
    );
  }

  if (isError || !item) {
    return (
      <Card variant="outlined" className={cn('p-6 sm:p-8', colors.border, 'max-w-md')}>
        <p className={cn('text-sm', colors.textSecondary)}>This delivery was not found or you no longer have access.</p>
        <Button type="button" variant="ghost" className="mt-4" onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4" />
          Back to list
        </Button>
      </Card>
    );
  }

  const lineCount = (item.lines || []).length;
  const supplierLabel = item.supplier
    ? item.supplier.name
    : `Supplier #${item.supplierId}`;

  return (
    <div className="space-y-6">
      <Card
        variant="outlined"
        className={cn('overflow-hidden border shadow-sm', colors.border)}
      >
        <div
          className={cn(
            'flex flex-col gap-4 border-b px-4 py-4 sm:px-6 sm:py-5',
            colors.border,
            'bg-gradient-to-b from-slate-50/80 to-white dark:from-slate-900/40 dark:to-slate-900/20',
          )}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('-ml-2 gap-1.5 self-start font-medium', colors.text)}
                onClick={goBack}
              >
                <ChevronLeftIcon className="h-4 w-4 opacity-80" />
                Back
              </Button>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              <Link
                to={moduleConfig.module.route.edit(item.id)}
                className={cn(
                  'inline-flex h-9 min-h-[2.25rem] shrink-0 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition-all hover:opacity-95',
                  colors.primary,
                  colors.primaryText,
                )}
              >
                Edit
              </Link>
            </div>
          </div>
        </div>
      </Card>

      {newRequisitionNoCaseTo ? <SupplyDeliveryShortfallAlert requisitionTo={newRequisitionNoCaseTo} /> : null}

      <Card variant="outlined" className={cn('p-4 shadow-sm sm:p-5', colors.border)}>
        <Accordion className="space-y-3">
          <AccordionItem
            title="Delivery details"
            description="Dates, case, vessel, port, purchase order, and notes."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mt-2 pt-1 sm:pt-0 ">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 text-sm md:grid-cols-12">
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Delivery (dlv.) date</dt>
                  <dd className={cn('mt-1', colors.text)}>{formatDate(item.dlvDate)}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Arrival / delivery date</dt>
                  <dd className={cn('mt-1', colors.text)}>{formatDate(item.deliveryDate)}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Reference</dt>
                  <dd className={cn('mt-1 break-words font-medium tabular-nums', colors.text)}>{item.ref || ''}</dd>
                </div>
                {item.refN != null ? (
                  <div className="min-w-0 md:col-span-3">
                    <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Ref #</dt>
                    <dd className={cn('mt-1 tabular-nums', colors.text)}>{item.refN}</dd>
                  </div>
                ) : null}
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Supply case</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.supplyCase?.id ? (
                      <Link
                        to={`/supplies/supply-cases/${item.supplyCase.id}/case-view`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.supplyCase.ref || `Case #${item.supplyCase.id}`}
                      </Link>
                    ) : item.caseId ? (
                      <span className="tabular-nums">#{item.caseId}</span>
                    ) : (
                      <span className={cn('italic', colors.textSecondary)}>—</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Vessel</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.vessel
                      ? [item.vessel.vesselName, item.vessel.vesselAbrv].filter(Boolean).join(' — ') || ''
                      : `Vessel #${item.vesselId}`}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Port</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.port
                      ? item.port.name
                      : ''}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Supplier</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>{supplierLabel}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Purchase order</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.order?.id ? (
                      <Link
                        to={`/supplies/supply-purchase-orders/${item.order.id}`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.order.ref || `PO #${item.order.id}`}
                      </Link>
                    ) : item.purchaseOrderId != null ? (
                      <span className="tabular-nums">#{item.purchaseOrderId}</span>
                    ) : (
                      <span className={cn('italic', colors.textSecondary)}>—</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-6 md:col-start-1">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Notes</dt>
                  <dd
                    className={cn('mt-1 min-h-[1.25rem] max-w-3xl whitespace-pre-wrap break-words', colors.text)}
                  >
                    {item.notes?.trim() ? item.notes : <span className={colors.textSecondary}>—</span>}
                  </dd>
                </div>
              </dl>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Line items"
            description="Store items, PO vs delivery quantities, delivery status, and line notes."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              lineCount > 0 ? (
                <CountChip className="mr-1.5">
                  {lineCount} line{lineCount === 1 ? '' : 's'}
                </CountChip>
              ) : null
            }
          >
            <div className="-mx-1 overflow-x-auto sm:mx-0 sm:px-0">
              <table className="w-full min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr
                    className={cn(
                      'border-b text-left text-xs font-semibold uppercase tracking-wider',
                      colors.border,
                      'bg-slate-50/95 dark:bg-slate-800/50',
                    )}
                  >
                    <th className="w-10 py-2.5 pl-1 pr-2 text-left tabular-nums sm:pl-0">#</th>
                    <th className="w-[10rem] min-w-[8rem] py-2.5 pl-0 pr-3">Level 1</th>
                    <th className="min-w-[12rem] py-2.5 pl-0 pr-3 sm:min-w-[16rem]">Item</th>
                    <th className="w-24 min-w-[5.5rem] py-2.5 pl-0 pr-3">Unit</th>
                    <th className="w-24 min-w-[5.5rem] py-2.5 pl-0 pr-3">PO qty</th>
                    <th className="w-24 min-w-[5.5rem] py-2.5 pl-0 pr-3">Dlv qty</th>
                    <th className="w-20 min-w-[4.5rem] py-2.5 pl-0 pr-3">Done</th>
                    <th className="min-w-[8rem] py-2.5 pl-0 pr-0 sm:pr-0">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(item.lines || []).length === 0 ? (
                    <tr>
                      <td colSpan={8} className={cn('py-8 text-center text-sm', colors.textSecondary)}>
                        No line items on this delivery.
                      </td>
                    </tr>
                  ) : (
                    (item.lines || []).map((line, lineIndex) => (
                      <tr
                        key={line.id ?? `${line.itemId}-${line.itemDescription}`}
                        className={cn(
                          'border-b text-left transition-colors last:border-0',
                          colors.border,
                          'hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                        )}
                      >
                        <td className={cn('py-2.5 pl-1 pr-2 text-left tabular-nums sm:pl-0', colors.textSecondary)}>{lineIndex + 1}</td>
                        <td className={cn('w-[10rem] min-w-[8rem] py-2.5 pl-0 pr-3 align-top', colors.text)}>
                          {(line.item?.level1 ?? '').trim() || ''}
                        </td>
                        <td
                          className={cn('min-w-[12rem] py-2.5 pl-0 pr-3 align-top sm:min-w-[16rem]', colors.text)}
                        >
                          {formatDeliveryItemCell(line)}
                        </td>
                        <td className={cn('w-24 py-2.5 pl-0 pr-3 align-top', colors.text)}>{(line.unit?.ucode ?? '').trim() || ''}</td>
                        <td className={cn('w-24 py-2.5 pl-0 pr-3 align-top tabular-nums', colors.text)}>{line.purchaseOrderQty ?? ''}</td>
                        <td className={cn('w-24 py-2.5 pl-0 pr-3 align-top tabular-nums', colors.text)}>{line.deliveryQty ?? ''}</td>
                        <td className={cn('w-20 py-2.5 pl-0 pr-3 align-top', colors.text)}>
                          {line.isDelivered ? (
                            <span
                              className="inline-flex items-center text-emerald-600 dark:text-emerald-400"
                              title="Delivered"
                              aria-label="Delivered"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </span>
                          ) : (
                            <span className={colors.textSecondary}>—</span>
                          )}
                        </td>
                        <td className={cn('max-w-[20rem] py-2.5 pl-0 pr-0 align-top', colors.text)}>{line.itemNotes || ''}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>
    </div>
  );
};

export default Detail;
